import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./api";

export type Operator = ">" | ">=" | "<" | "<=" | "==" | "!=" | "exists";

export interface ApprovalCondition {
  field: string;
  operator: Operator;
  value?: number | string;
}

/** What a matching rule does. Rules from before TASK-207 are "review". */
export type RuleAction = "allow" | "reject" | "review";

export interface Rule {
  id: number;
  event_type: string;
  condition: ApprovalCondition | null;
  is_global: boolean;
  /** Specific projects (empty when is_global). */
  project_ids: number[];
  project_names: string[];
  active: boolean;
  created_at: string;
  action: RuleAction;
  /** Optional display name; the shipped rules have one. */
  name: string | null;
  /** Shipped with the system ("mitgeliefert"). */
  builtin: boolean;
}

export interface RuleInput {
  isGlobal: boolean;
  /** When isGlobal is false: list of project IDs to apply this rule to. */
  projectIds: number[];
  eventType: string;
  condition: ApprovalCondition | null;
  action: RuleAction;
  name?: string;
  /** Optional override for which project's BQ data the live preview samples from. */
  previewProject?: number;
}

export type RulePreviewResponse =
  | {
      unavailable?: false;
      sampledFromDays: number;
      sampleSize: number;
      matchedCount: number;
      samples: Array<{
        event_type: string;
        created_at: string;
        summary: string;
      }>;
    }
  /** Field the preview cannot compute (test_contact). */
  | { unavailable: true; message: string };

const RULES_KEY = ["admin", "rules"] as const;

export function useRules(
  filters: { project?: number; eventType?: string; includeInactive?: boolean } = {},
) {
  return useQuery<{ items: Rule[] }>({
    queryKey: [...RULES_KEY, filters],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filters.project) p.set("project", String(filters.project));
      if (filters.eventType) p.set("eventType", filters.eventType);
      if (filters.includeInactive) p.set("includeInactive", "1");
      const qs = p.toString();
      return api(`/admin/rules${qs ? `?${qs}` : ""}`);
    },
    staleTime: 30_000,
  });
}

export function useRule(id: number | null) {
  return useQuery<Rule>({
    queryKey: ["admin", "rules", "single", id],
    queryFn: () => api<Rule>(`/admin/rules/${id}`),
    enabled: id !== null,
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleInput) =>
      api<{ ok: true; id: number }>("/admin/rules", {
        method: "POST",
        body: JSON.stringify({
          isGlobal: input.isGlobal,
          projectIds: input.projectIds,
          eventType: input.eventType,
          condition: input.condition,
          action: input.action,
          name: input.name,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useUpdateRule(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleInput) =>
      api<{ ok: true }>(`/admin/rules/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          isGlobal: input.isGlobal,
          projectIds: input.projectIds,
          eventType: input.eventType,
          condition: input.condition,
          action: input.action,
          name: input.name,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{ ok: true }>(`/admin/rules/${id}/activate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{ ok: true }>(`/admin/rules/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
  });
}

export function useRulePreview(input: RuleInput | null) {
  return useQuery<RulePreviewResponse>({
    queryKey: ["admin", "rules", "preview", input],
    queryFn: () =>
      api<RulePreviewResponse>("/admin/rules/preview", {
        method: "POST",
        body: JSON.stringify({
          isGlobal: input!.isGlobal,
          projectIds: input!.projectIds,
          eventType: input!.eventType,
          condition: input!.condition,
          previewProject: input!.previewProject,
        }),
      }),
    enabled:
      input !== null &&
      input.eventType.length > 0 &&
      (input.condition === null ||
        (input.condition.field.length > 0 &&
          (input.condition.operator === "exists" ||
            input.condition.value !== undefined))) &&
      // need a concrete project for BQ sampling
      ((!input.isGlobal && input.projectIds.length > 0) ||
        input.previewProject !== undefined),
    staleTime: 30_000,
  });
}
